import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, Phone, Mail, MapPin, Clock, Loader2, ShieldCheck,
} from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, MaskedLine } from "@/components/Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VEHICLE_TYPES = [
  "Car", "Van", "Motorcycle", "Plant & Machinery", "Caravan",
  "Boat", "Prestige / Classic (enclosed)", "Other",
];

const initialForm = {
  name: "", email: "", phone: "", vehicle_type: "Car",
  collection: "", delivery: "", preferred_date: "", message: "",
};

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors duration-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    {children}
  </label>
);

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        preferred_date: form.preferred_date || null,
        message: form.message || null,
      });
      toast.success("Quote request sent — we'll be in touch within one working day.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong sending your request. Please call us on +44 7462 227041.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact & Free Quotes | Solaris Transport LTD"
        description="Request a free, customised vehicle transport quote from Solaris Transport LTD, Tadcaster. Call +44 7462 227041 or send the form — replies within one working day."
        path="/contact"
      />

      <section className="border-b border-slate-200 bg-white" data-testid="contact-hero">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-700" data-testid="contact-eyebrow">Contact &amp; quotes</p>
          <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="contact-headline">
            <MaskedLine delay={0.1}>Tell us what</MaskedLine>
            <MaskedLine delay={0.22}>you're <span className="text-blue-700">moving.</span></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            Fill in the form and our operations team will come back with a
            fixed, customised quote — usually within one working day.
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24" data-testid="contact-main-section">
        <div className="grid gap-10 lg:grid-cols-12">
          {/* Form */}
          <FadeUp className="lg:col-span-7">
            <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10" data-testid="quote-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full name" testid="field-name">
                  <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Smith" data-testid="quote-form-name-input" />
                </Field>
                <Field label="Phone" testid="field-phone">
                  <input required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="07700 900000" data-testid="quote-form-phone-input" />
                </Field>
                <Field label="Email" testid="field-email">
                  <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@example.co.uk" data-testid="quote-form-email-input" />
                </Field>
                <Field label="Vehicle / equipment type" testid="field-vehicle-type">
                  <select value={form.vehicle_type} onChange={set("vehicle_type")} className={inputCls} data-testid="quote-form-vehicle-type-select">
                    {VEHICLE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </Field>
                <Field label="Collection — City or Postcode" testid="field-collection">
                  <input required value={form.collection} onChange={set("collection")} className={inputCls} placeholder="e.g. Tadcaster or LS24 8BN" data-testid="quote-form-collection-input" />
                </Field>
                <Field label="Delivery — City or Postcode" testid="field-delivery">
                  <input required value={form.delivery} onChange={set("delivery")} className={inputCls} placeholder="e.g. Manchester or M1 1AA" data-testid="quote-form-delivery-input" />
                </Field>
                <Field label="Preferred date (optional)" testid="field-date">
                  <input type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} data-testid="quote-form-date-input" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Special requirements (optional)" testid="field-message">
                    <textarea rows={4} value={form.message} onChange={set("message")} className={inputCls} placeholder="Non-runner, tight access, specific time window…" data-testid="quote-form-message-textarea" />
                  </Field>
                </div>
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="quote-form-submit-button"
                className="group mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-blue-700 px-7 py-4 text-base font-bold text-white transition-colors duration-200 hover:bg-blue-800 disabled:opacity-60 sm:w-auto"
              >
                {sending ? (
                  <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</>
                ) : (
                  <>Request my free quote <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" /></>
                )}
              </button>
              <p className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                <ShieldCheck className="h-4 w-4 text-blue-700" />
                Every booking includes independent, FCA-regulated payment protection.
              </p>
            </form>
          </FadeUp>

          {/* Info */}
          <div className="space-y-5 lg:col-span-5">
            <FadeUp delay={0.1}>
              <div className="rounded-3xl bg-slate-950 p-8 text-white" data-testid="contact-info-card">
                <h2 className="font-display text-lg font-bold">Talk to operations</h2>
                <ul className="mt-6 space-y-5 text-sm">
                  <li className="flex gap-3">
                    <Phone className="mt-0.5 h-5 w-5 shrink-0 text-blue-400" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Phone</p>
                      <a href="tel:+447462227041" data-testid="contact-phone-link" className="mt-1 block font-semibold text-white hover:text-blue-300">+44 7462 227041</a>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <Mail className="mt-0.5 h-5 w-5 shrink-0 text-blue-400" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Email</p>
                      <a href="mailto:operations@solaris-transport-ltd.co.uk" data-testid="contact-email-link" className="mt-1 block break-all font-semibold text-white hover:text-blue-300">operations@solaris-transport-ltd.co.uk</a>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-blue-400" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Depot</p>
                      <p className="mt-1 leading-relaxed text-slate-200" data-testid="contact-address">
                        Unit 2 Priory Farm, Featherbed Lane,<br />
                        Wighill Park, Tadcaster,<br />
                        England, LS24 8BN
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </FadeUp>

            <FadeUp delay={0.2}>
              <div className="rounded-3xl border border-slate-200 bg-white p-8" data-testid="contact-hours-card">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-blue-700" />
                  <h2 className="font-display text-lg font-bold text-slate-900">Opening hours</h2>
                </div>
                <dl className="mt-5 space-y-3 text-sm">
                  <div className="flex justify-between border-b border-slate-100 pb-3">
                    <dt className="text-slate-600">Monday – Friday</dt>
                    <dd className="font-mono font-semibold text-slate-900" data-testid="hours-weekday">9:00am – 5:00pm</dd>
                  </div>
                  <div className="flex justify-between border-b border-slate-100 pb-3">
                    <dt className="text-slate-600">Saturday</dt>
                    <dd className="font-mono font-semibold text-slate-900" data-testid="hours-saturday">9:00am – 2:00pm</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-600">Sunday</dt>
                    <dd className="font-mono font-semibold text-slate-400" data-testid="hours-sunday">Closed</dd>
                  </div>
                </dl>
                <p className="mt-5 rounded-xl bg-blue-50 px-4 py-3 text-xs leading-relaxed text-blue-800">
                  Collections and deliveries run seven days a week by arrangement — the office hours above are for quotes and bookings.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
